import type { MappingResult } from '../recognize/config';
import type { ChainMapping } from '../schema/mapping';
import { getEffectiveSolverType, SolverType } from '../schema/solver';

const LEG_CHAIN_NAMES = ['LeftLeg', 'RightLeg'] as const;
const LEG_CHAIN_SIDE_BY_NAME = {
  LeftLeg: 'Left',
  RightLeg: 'Right',
} as const;

export type CartographerLegChainName = (typeof LEG_CHAIN_NAMES)[number];

type LegModeSnapshotSource =
  | Pick<MappingResult, 'roleToBone' | 'chains' | 'chainResults'>
  | {
      roleToBone: Record<string, string>;
      chains: Record<string, string[]>;
      chainResults?: Record<string, ChainMapping>;
    }
  | null;

export interface CartographerLegModeState {
  exists: boolean;
  hasHock: boolean;
  hasHockRole: boolean;
  effectiveSolverType: string | null;
}

export type CartographerLegModeSnapshot = Record<
  CartographerLegChainName,
  CartographerLegModeState
>;

export interface CartographerLegSolverAutoSwitch {
  chainName: CartographerLegChainName;
  solverType: SolverType;
}

function emptyLegModeState(): CartographerLegModeState {
  return {
    exists: false,
    hasHock: false,
    hasHockRole: false,
    effectiveSolverType: null,
  };
}

function captureLegModeState(
  source: LegModeSnapshotSource,
  chainName: CartographerLegChainName,
): CartographerLegModeState {
  const chainBoneNames = source?.chains[chainName] ?? [];
  if (chainBoneNames.length === 0) return emptyLegModeState();

  const side = LEG_CHAIN_SIDE_BY_NAME[chainName];
  const hockRoleName = `${side}Hock`;
  const lowerLegRoleName = `${side}LowerLeg`;
  const footRoleName = `${side}Foot`;
  const hockBoneName = source?.roleToBone[hockRoleName] ?? null;
  const lowerLegBoneName = source?.roleToBone[lowerLegRoleName] ?? null;
  const footBoneName = source?.roleToBone[footRoleName] ?? null;
  const hasHockRole = hockBoneName !== null && chainBoneNames.includes(hockBoneName);
  const lowerLegIndex =
    lowerLegBoneName === null ? -1 : chainBoneNames.indexOf(lowerLegBoneName);
  const footIndex = footBoneName === null ? -1 : chainBoneNames.indexOf(footBoneName);
  const hasInteriorLegSegment =
    lowerLegIndex >= 0 && footIndex >= 0 && footIndex - lowerLegIndex >= 2;
  const hasHock = hasHockRole || hasInteriorLegSegment || chainBoneNames.length >= 4;
  const effectiveSolverType = getEffectiveSolverType(
    chainName,
    source?.chainResults?.[chainName]?.solverType ?? null,
  );

  return {
    exists: true,
    hasHock,
    hasHockRole,
    effectiveSolverType,
  };
}

export function captureCartographerLegModeSnapshot(
  source: LegModeSnapshotSource,
): CartographerLegModeSnapshot {
  return {
    LeftLeg: captureLegModeState(source, 'LeftLeg'),
    RightLeg: captureLegModeState(source, 'RightLeg'),
  };
}

export function getCartographerLegSolverAutoSwitches(
  before: CartographerLegModeSnapshot,
  after: CartographerLegModeSnapshot,
): CartographerLegSolverAutoSwitch[] {
  const switches: CartographerLegSolverAutoSwitch[] = [];

  for (const chainName of LEG_CHAIN_NAMES) {
    const previous = before[chainName];
    const next = after[chainName];
    if (!next.exists || previous.hasHock === next.hasHock) continue;

    const solverType = next.hasHock ? SolverType.Z : SolverType.Tri;
    if (next.effectiveSolverType === solverType) continue;

    switches.push({ chainName, solverType });
  }

  return switches;
}
